import React, { useState, useEffect, useCallback } from 'react';
import {
  Download,
  FileSpreadsheet,
  RefreshCw,
  Printer,
  Search,
  ArrowUpRight,
  ArrowDownLeft
} from 'lucide-react';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getStatementOfAccount } from '../../api/reports';
import { useBranding } from '../../contexts/BrandingContext';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import { toast } from 'react-toastify';
import PeriodFilter from '../../components/common/PeriodFilter';
import Pagination from '../../components/common/Pagination';
import BlurredValue from '../../components/common/BlurredValue';

const StatementOfAccountPage = () => {
  const { config: branding } = useBranding();
  const { periodRange, getDateRangeParams } = useFiscalPeriod();
  const [localPeriodRange, setLocalPeriodRange] = useState(null);

  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [includeSales, setIncludeSales] = useState(true);
  const [includeExpenses, setIncludeExpenses] = useState(true);
  const [includeSalaries, setIncludeSalaries] = useState(true);
  const [includeLoans, setIncludeLoans] = useState(true);
  const [includeOther, setIncludeOther] = useState(true);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const itemsPerPage = 50;

  const fetchStatement = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let params = getDateRangeParams();
      if (localPeriodRange && !localPeriodRange.isGlobal) {
        params = {
          startDate: localPeriodRange.start_date,
          endDate: localPeriodRange.end_date
        };
      }

      const filters = {
        include_sales: includeSales,
        include_expenses: includeExpenses,
        include_salaries: includeSalaries,
        include_loans: includeLoans,
        include_other: includeOther
      };

      const response = await getStatementOfAccount(
        params.startDate || params.start_date,
        params.endDate || params.end_date,
        filters
      );
      setStatement(response.data || response);
    } catch (err) {
      console.error('Error fetching statement:', err);
      setError('Failed to load statement data.');
      toast.error('Failed to load statement.');
    } finally {
      setLoading(false);
    }
  }, [
    getDateRangeParams,
    localPeriodRange,
    includeSales,
    includeExpenses,
    includeSalaries,
    includeLoans,
    includeOther
  ]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    periodRange,
    localPeriodRange,
    includeSales,
    includeExpenses,
    includeSalaries,
    includeLoans,
    includeOther
  ]);

  const generatePDF = async () => {
    if (!statement) return;

    try {
      toast.info('Preparing statement PDF download...');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const accentColor = branding.accent_color || '#D4AF37';
      const [r, g, b] = [
        parseInt(accentColor.slice(1, 3), 16),
        parseInt(accentColor.slice(3, 5), 16),
        parseInt(accentColor.slice(5, 7), 16)
      ];

      // Header
      doc.setFontSize(22);
      doc.setTextColor(r, g, b);
      doc.text(branding.company_name || 'Eldaved Global', 14, 20);

      doc.setFontSize(16);
      doc.setTextColor(40, 40, 40);
      doc.text('RETAIL STATEMENT OF ACCOUNT', 14, 30);

      doc.setFontSize(11);
      doc.setTextColor(80, 80, 80);
      let dateParams = getDateRangeParams();
      if (localPeriodRange && !localPeriodRange.isGlobal) {
        dateParams = {
          date_from: localPeriodRange.start_date,
          date_to: localPeriodRange.end_date
        };
      }
      let periodText = 'All Time';
      if (dateParams.date_from && dateParams.date_to) {
        periodText = `${format(new Date(dateParams.date_from), 'MMM dd, yyyy')} to ${format(new Date(dateParams.date_to), 'MMM dd, yyyy')}`;
      } else if (dateParams.date_from) {
        periodText = `From ${format(new Date(dateParams.date_from), 'MMM dd, yyyy')}`;
      }
      doc.text(`Period: ${periodText}`, 14, 40);
      doc.text(`Generated: ${format(new Date(), 'MMM dd, yyyy HH:mm')}`, 14, 46);

      const filters = {
        include_sales: includeSales,
        include_expenses: includeExpenses,
        include_salaries: includeSalaries,
        include_other: includeOther
      };

      const fullDataResponse = await getStatementOfAccount(
        dateParams.date_from || dateParams.startDate,
        dateParams.date_to || dateParams.endDate,
        filters
      );
      const fullStatement = fullDataResponse.data || fullDataResponse;

      // Summary Table
      autoTable(doc, {
        startY: 55,
        head: [
          [
            'Opening Balance',
            'Total Inflows',
            'Total Outflows',
            'Sales Revenue',
            'Expenses',
            'Payroll Payments',
            'Closing Balance'
          ]
        ],
        body: [
          [
            `N ${(fullStatement.opening_balance || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_inflows || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_outflows || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_sales || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_expenses || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_payroll || 0).toLocaleString()}`,
            `N ${(fullStatement.closing_balance || 0).toLocaleString()}`
          ]
        ],
        theme: 'grid',
        headStyles: { fillColor: [240, 240, 240], textColor: [40, 40, 40], fontStyle: 'bold' },
        styles: { halign: 'right' }
      });

      // Detail Table
      const tableColumn = [
        'Date',
        'Reference',
        'Type',
        'Description',
        'Payment Method',
        'Inflow (N)',
        'Outflow (N)',
        'Balance (N)'
      ];

      const timelineList = [...fullStatement.timeline];
      if (sortOrder === 'desc') {
        timelineList.reverse();
      }

      const tableRows = timelineList.map((item) => {
        const inflow = item.type === 'inflow' ? `${parseFloat(item.amount).toLocaleString()}` : '-';
        const outflow =
          item.type === 'outflow' ? `${parseFloat(item.amount).toLocaleString()}` : '-';
        return [
          format(new Date(item.date), 'MMM dd, yyyy'),
          item.reference || '',
          (item.source_type || '').toUpperCase(),
          item.remark || '',
          (item.payment_method || '').toUpperCase(),
          inflow,
          outflow,
          (item.running_balance || 0).toLocaleString()
        ];
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [tableColumn],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [r, g, b], textColor: 255 },
        styles: { fontSize: 8, cellPadding: 2.5 },
        columnStyles: {
          5: { halign: 'left' },
          6: { halign: 'left' },
          7: { halign: 'left', fontStyle: 'bold' }
        },
        margin: { top: 15, right: 10, bottom: 15, left: 10 }
      });

      doc.save(`Retail_Statement_${format(new Date(), 'yyyyMMdd')}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Failed to generate PDF');
    }
  };

  const generateCSV = async () => {
    if (!statement) return;

    try {
      toast.info('Preparing CSV download...');
      let dateParams = getDateRangeParams();
      if (localPeriodRange && !localPeriodRange.isGlobal) {
        dateParams = {
          date_from: localPeriodRange.start_date,
          date_to: localPeriodRange.end_date
        };
      }

      const filters = {
        include_sales: includeSales,
        include_expenses: includeExpenses,
        include_salaries: includeSalaries,
        include_loans: includeLoans,
        include_other: includeOther
      };

      const fullDataResponse = await getStatementOfAccount(
        dateParams.date_from || dateParams.startDate,
        dateParams.date_to || dateParams.endDate,
        filters
      );
      const fullStatement = fullDataResponse.data || fullDataResponse;

      const timelineList = [...fullStatement.timeline];
      if (sortOrder === 'desc') {
        timelineList.reverse();
      }

      const headers = [
        'Date',
        'Reference',
        'Type',
        'Description',
        'Payment Method',
        'Inflow',
        'Outflow',
        'Balance'
      ];
      const rows = timelineList.map((item) => [
        format(new Date(item.date), 'yyyy-MM-dd'),
        item.reference || '',
        item.source_type || '',
        `"${item.remark || ''}"`,
        item.payment_method || '',
        item.type === 'inflow' ? item.amount : '',
        item.type === 'outflow' ? item.amount : '',
        item.running_balance
      ]);

      const csvContent = [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `Retail_Statement_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error generating CSV:', err);
      toast.error('Failed to generate CSV');
    }
  };

  const sortedTimeline = React.useMemo(() => {
    if (!statement?.timeline) return [];
    const list = [...statement.timeline];
    if (sortOrder === 'desc') {
      return list.reverse();
    }
    return list;
  }, [statement?.timeline, sortOrder]);

  const totalPages = Math.ceil(sortedTimeline.length / itemsPerPage);
  const paginatedTimeline = sortedTimeline.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Statement of Account</h1>
          <p className="text-gray-500 mt-1">
            General cash, bank, expense, and payroll transactions ledger
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Select / Filter Statement Items
        </label>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2.5 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeSales}
              onChange={(e) => setIncludeSales(e.target.checked)}
              className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
            />
            <span className="text-sm font-medium text-gray-700">Sales & Inflows</span>
          </label>

          <label className="flex items-center gap-2.5 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeExpenses}
              onChange={(e) => setIncludeExpenses(e.target.checked)}
              className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
            />
            <span className="text-sm font-medium text-gray-700">General Expenses</span>
          </label>

          <label className="flex items-center gap-2.5 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeSalaries}
              onChange={(e) => setIncludeSalaries(e.target.checked)}
              className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
            />
            <span className="text-sm font-medium text-gray-700">Salaries & Payroll</span>
          </label>

          <label className="flex items-center gap-2.5 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeLoans}
              onChange={(e) => setIncludeLoans(e.target.checked)}
              className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
            />
            <span className="text-sm font-medium text-gray-700">Loans (Granted & Repayments)</span>
          </label>

          <label className="flex items-center gap-2.5 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeOther}
              onChange={(e) => setIncludeOther(e.target.checked)}
              className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
            />
            <span className="text-sm font-medium text-gray-700">
              Other Transactions (Deposits, Withdrawals, Transfers)
            </span>
          </label>
        </div>
      </div>

      {/* Actions & Filters */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <button
            onClick={fetchStatement}
            className="p-2 text-gray-500 hover:text-accent-gold hover:bg-accent-gold/10 rounded-lg transition-colors"
            title="Refresh"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-1.5 select-none font-medium"
            title={`Sorting: ${sortOrder === 'asc' ? 'Oldest First' : 'Newest First'}`}
          >
            Sort: {sortOrder === 'asc' ? 'Oldest ➔ Newest' : 'Newest ➔ Oldest'}
          </button>
          <span className="text-sm text-gray-600 border-l border-gray-300 px-2">Filters:</span>
          <PeriodFilter
            onChange={setLocalPeriodRange}
            storageKey="retail_statement_period"
            compact={true}
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={generateCSV}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <FileSpreadsheet className="w-4 h-4 text-green-600" />
            CSV Export
          </button>
          <button
            onClick={generatePDF}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors text-sm font-medium shadow-sm"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </div>
      </div>

      {/* Summary Grid */}
      {statement && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Opening Balance</p>
            <p
              className={`text-lg font-bold ${(statement.opening_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              <BlurredValue
                value={`₦${Math.round(statement.opening_balance || 0).toLocaleString()}`}
              />
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-xs text-emerald-600 mb-1">Total Inflows</p>
            <p className="text-lg font-bold text-emerald-600">
              <BlurredValue
                value={`₦${(statement.summary?.total_inflows || 0).toLocaleString()}`}
              />
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-xs text-red-600 mb-1">Total Outflows</p>
            <p className="text-lg font-bold text-red-600">
              <BlurredValue
                value={`₦${(statement.summary?.total_outflows || 0).toLocaleString()}`}
              />
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-xs text-orange-600 mb-1">Expenses</p>
            <p className="text-lg font-bold text-gray-900">
              <BlurredValue
                value={`₦${(statement.summary?.total_expenses || 0).toLocaleString()}`}
              />
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-xs text-red-500 mb-1">Payroll</p>
            <p className="text-lg font-bold text-gray-900">
              <BlurredValue
                value={`₦${(statement.summary?.total_payroll || 0).toLocaleString()}`}
              />
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-orange-100 shadow-sm">
            <p className="text-xs text-orange-500 mb-1">Loans Granted</p>
            <p className="text-lg font-bold text-orange-600">
              <BlurredValue
                value={`₦${(statement.summary?.total_loans_granted || 0).toLocaleString()}`}
              />
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-teal-100 shadow-sm">
            <p className="text-xs text-teal-600 mb-1">Loan Repayments</p>
            <p className="text-lg font-bold text-teal-600">
              <BlurredValue
                value={`₦${(statement.summary?.total_loan_repayments || 0).toLocaleString()}`}
              />
            </p>
          </div>
          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 shadow-sm">
            <p className="text-xs text-emerald-800 font-semibold mb-1">Closing Balance</p>
            <p
              className={`text-xl font-black ${(statement.closing_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              <BlurredValue
                value={`₦${Math.round(statement.closing_balance || 0).toLocaleString()}`}
              />
            </p>
          </div>
        </div>
      )}

      {/* Ledger Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Reference
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Method
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">
                  Inflow (₦)
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">
                  Outflow (₦)
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase font-bold">
                  Balance (₦)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading && paginatedTimeline.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-gray-500 text-sm">
                    Refreshing ledger data...
                  </td>
                </tr>
              ) : paginatedTimeline.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-gray-500 text-sm">
                    No transactions found for the selected period.
                  </td>
                </tr>
              ) : (
                paginatedTimeline.map((item, idx) => {
                  return (
                    <tr key={`${item.id}-${idx}`} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {format(new Date(item.date), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium font-mono">
                        {item.reference}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            item.source_type === 'sale'
                              ? 'bg-emerald-100 text-emerald-800'
                              : item.source_type === 'expense'
                                ? 'bg-orange-100 text-orange-800'
                                : item.source_type === 'payroll'
                                  ? 'bg-red-100 text-red-800'
                                  : item.source_type === 'payroll_reversal'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {item.source_type === 'payroll_reversal'
                            ? 'payroll reversal'
                            : item.source_type}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate"
                        title={item.remark}
                      >
                        {item.remark}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 uppercase">
                        {item.payment_method}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-emerald-600">
                        {item.type === 'inflow' ? (
                          <BlurredValue value={parseFloat(item.amount).toLocaleString()} />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-red-500">
                        {item.type === 'outflow' ? (
                          <BlurredValue value={parseFloat(item.amount).toLocaleString()} />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td
                        className={`px-4 py-3 text-right text-sm font-bold bg-gray-50/50 dark:bg-slate-800/50 border-l border-gray-100 dark:border-slate-800 ${
                          parseFloat(item.running_balance) > 0
                            ? 'text-green-600 dark:text-green-400'
                            : parseFloat(item.running_balance) < 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        <BlurredValue value={parseFloat(item.running_balance).toLocaleString()} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {statement?.timeline?.length > itemsPerPage && (
          <div className="p-4 border-t">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={statement.timeline.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              itemLabel="transactions"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StatementOfAccountPage;
